/**
 * Morse — der Reiter „Hören“. (06.10.2026, Morse-Überarbeitung)
 *
 * Martin: „Ich kann Geben und Hören noch nicht so ganz nutzen. Start und Stopp geht noch nicht so ganz.“
 * Vorher: „Abspielen“ spielte 25 Zeichen am Stück (31 s auf Stufe 1), ein zweiter Tipp begann von vorn, einen
 * Stopp gab es nicht, getippt wurde danach aus dem Gedächtnis. Keine einzige Übung wurde gebucht.
 *
 * Jetzt (LCWO, Koch-Trainer):
 *   - „Start“ spielt eine Runde aus zehn Gruppen, Gruppe für Gruppe von selbst. Mittippen geht; nach jeder
 *     Gruppe bleibt Zeit zum Tippen (die Leiste zeigt, wie viel). Enter oder Leertaste im Feld = nächste Gruppe
 *     sofort. Der Fokus bleibt im Feld (am iPhone bleibt so die Tastatur offen).
 *   - Nach jeder Gruppe die Rückmeldung Zeichen für Zeichen; was falsch war, klingt gleich langsam nochmal.
 *   - „Stopp“ hält SOFORT an — kein Ton, kein geplanter Ton, keine Uhr läuft weiter (Zähler `token`) — und zeigt
 *     die Zusammenfassung; gebucht wird, was beantwortet ist. „Pause“ hält an, „Weiter“ spielt die Gruppe neu.
 *   - Seite verdeckt oder Reiter gewechselt: die Runde pausiert (nichts geht verloren).
 *   - „Sprechen“ (08.10.2026, morse-sprechen.ts): NACH dem Ton darf die Antwort auch gesprochen werden — am Stück oder Zeichen
 *     für Zeichen. Das Ergebnis steht im Feld (unsichere Stellen leer, `·`) und geht NIE von selbst ab: die Frist beginnt neu
 *     (`SPRECH_PRUEFEN_MS`). Während das Mikrofon hört, ruht die Frist, und der Ton darf schlafen (`lassWach`); danach wieder wach.
 *
 * ⚠️ Der Kontext bleibt während der Runde wach (`wachHalten`): sonst schliefe er in der App zwischen zwei Gruppen
 *    und jede Gruppe müsste ihn ohne Geste wecken.
 */
import {
  kochAuswertung,
  kochNeueZeichen,
  kochNeuesZeichen,
  kochTempoVorgabe,
  kochUebungsZeichen,
  kochWoerter,
  KOCH_ANFAENGER_BIS_STUFE,
  KOCH_LETZTE_STUFE,
  KOCH_WOERTER_AB,
  type Auswertung,
  type Umfang,
} from "../../shared/morse-koch.js";
import { MORSE_TABELLE } from "../../shared/morse.js";
import { musterBild, musterSprechen } from "./morse-bild.js";
import { baueSpick, liesHilfe, liesModus, merkeHilfe, merkeKennengelernt, merkeModus, zeichneSpick, type HoerHilfe, type HoerModus } from "./morse-hilfe.js";
import { MorseAugenZu } from "./morse-augen-zu.js";
import { MorseKennenlernen } from "./morse-kennenlernen.js";
import { MorseStufe } from "./morse-stufe.js";
import { MorseUmfang } from "./morse-umfang.js";
import { ANLEITUNG, AUGEN, HILFE, SPRECHEN, TEMPO, UMFANG } from "./morse-texte.js";
import { MorseSprechen, type SprechKontext } from "./morse-sprechen.js";
import { LUECKE } from "./morse-sprechen-leser.js";
import {
  eingabeZeitMs,
  fristNachEingabe,
  GNADE_MS,
  hoerRunde,
  hoerZeiten,
  rundeAlsAufgaben,
  rundeErgebnis,
  SPRECH_PRUEFEN_MS,
  SPRECH_PRUEFEN_UNSICHER_MS,
  zuBuchen,
  unsichereZeichen,
  VORLAUF_MS,
  zeichenVon,
  type Antwort,
  type HoerRunde,
  type HoerZeiten,
} from "./morse-runde.js";
import { Ablauf } from "./morse-ablauf.js";
import { rundenVerlauf } from "./morse-balken.js";
import type { HoerspurPlatz } from "./morse-hoerspur-ansicht.js";
import {
  baueBuehne,
  insBild,
  baueLeiste,
  hoereMitMeldung,
  dauerText,
  el,
  fokusAufStart,
  keinTonText,
  knopf,
  laufeLeiste,
  prozent,
  vergleich,
  wahl,
  zeichenKnopf,
  type Buehne,
  type BuehnenZustand,
  type MorseUebung,
  type MorseUmgebung,
} from "./morse-teile.js";

/** Ab welcher Stufe es Wortaufgaben gibt. */
function ersteWortStufe(): number {
  for (let s = 1; s <= KOCH_LETZTE_STUFE; s++) if (kochWoerter(s).length >= KOCH_WOERTER_AB) return s;
  return KOCH_LETZTE_STUFE;
}

/** Gibt es auf dieser Stufe Wörter MIT dem neuen Zeichen? Sonst kommen Gruppen (shared/morse-koch.ts). */
function woerterMitNeu(stufe: number): boolean {
  const w = kochWoerter(stufe);
  return w.length >= KOCH_WOERTER_AB && w.some((x) => x.includes(kochNeuesZeichen(stufe)));
}

type Phase = "hoert" | "tippt" | "rueckmeldung";

/** Der Name des Reiters in den Diagnosezeilen — EINMAL, ASCII (greppbar: „[morse] … reiter=hoeren …“). */
const REITER = "hoeren";

/** So viele falsche Zeichen klingen nach einer Gruppe noch einmal langsam. */
const NOCHMAL_HOECHSTENS = 3;
type Art = "gruppen" | "woerter";

export class MorseHoeren implements MorseUebung {
  private readonly b: Buehne;
  private readonly bereitTeil: HTMLElement;
  private vorfuehrung: HoerspurPlatz | null = null;
  private readonly zeichenTitel: HTMLElement;
  private readonly zeichenReihe: HTMLElement;
  private readonly zeichenLegende: HTMLElement;
  /** „Üben mit“: Meine Zeichen | Alle Zeichen (08.10.2026) — die Wahl, und was die laufende Runde beim Start davon nahm. */
  private readonly umfangWahl: MorseUmfang;
  private umfang: Umfang = "meine";
  private readonly artWahl: ReturnType<typeof wahl<Art>>;
  private readonly artHinweis: HTMLElement;
  private readonly anleitung: HTMLElement;
  private readonly laufTeil: HTMLElement;
  private readonly phaseZeile: HTMLElement;
  private readonly eingabe: HTMLInputElement;
  private readonly frist: HTMLElement;
  private readonly fertigTeil: HTMLElement;
  private readonly liste: HTMLOListElement;
  /** Kennenlernen: das neue Zeichen der Stufe in Ruhe, bevor geübt wird. */
  private readonly kennen: MorseKennenlernen;
  /** Nach einem Aufstieg: „Zu viel auf einmal? Eine Stufe zurück“ (08.10.2026) — sonst ausgeblendet. */
  private readonly stufeZeile: MorseStufe | null;
  /** K1: ist das eigene Tempo auf Stufe 1–2 schneller als der sanfte Start, sagt es EINE Zeile — mit „Vorgabe nehmen“. */
  private readonly tempoHinweis: HTMLElement;
  private readonly tempoText: HTMLElement;
  /** Hilfe: die Wahl (Bereit) und — in der Runde — das Muster der Gruppe und der Spickzettel. */
  private readonly hilfeWahl: ReturnType<typeof wahl<HoerHilfe>>;
  private readonly hilfeText: HTMLElement;
  private readonly hilfeTeil: HTMLElement;
  private readonly hilfeTitel: HTMLElement;
  private readonly hilfeMuster: HTMLElement;
  private readonly spick: HTMLElement;
  private hilfe: HoerHilfe = "aus";
  /** Zeit zum Tippen und Luft zwischen den Gruppen — für Anfänger länger (morse-runde.ts, `hoerZeiten`). */
  private zeiten: HoerZeiten = hoerZeiten(KOCH_LETZTE_STUFE);
  /** Das Tempo beim Start — bringt ein Aufstieg ein anderes (Vorgabe der Stufe), sagt es die Meldung. */
  private tempoBeimStart = "";

  private art: Art = "gruppen";
  private zustand: BuehnenZustand = "bereit";
  private runde: HoerRunde | null = null;
  private antworten: Array<Antwort | undefined> = [];
  private index = 0;
  private phaseWert: Phase | null = null;
  /** Der Knopf „Sprechen“ (morse-sprechen.ts) — nur wo die Umgebung Mikrofon und Mitschrift stellt. */
  private readonly sprechen: MorseSprechen | null;
  /** Das Mikrofon hat den Ton aus der Pflicht genommen (`lassWach`): vor der nächsten Gruppe wieder wach halten. */
  private wachGeloest = false;
  /** Token, Uhr, wacher Ton, aktive Zeit, Buchung (morse-ablauf.ts). */
  private readonly ablauf: Ablauf;
  private fristBis = 0;
  private leisteHalt: (() => void) | null = null;
  private weiterBei = 0;
  /** In welcher Phase pausiert wurde — eine ganz gehörte Gruppe zählt beim Stopp auch leer (K4). */
  private pausiertIn: Phase | null = null;
  /** Gruppen, die schon in einer Buchung stecken (gesendet oder vorgemerkt) — keine zweimal (W4). */
  private gebucht = new Set<number>();
  /** Welche Runde das ist — eine spätere Antwort des Servers schreibt nicht in eine neue Runde. */
  private rundeNr = 0;
  private readonly tastenLauf: HTMLElement;
  /** „Augen zu“ (13.10.2026, morse-augen-zu.ts): eine eigene Bühne neben dieser — nur wo Mikrofon und Mitschrift da sind. */
  private readonly augen: MorseAugenZu | null;
  private readonly modusWahl: ReturnType<typeof wahl<HoerModus>> | null;
  private modus: HoerModus = "tippen";

  constructor(
    private readonly u: MorseUmgebung,
    tafel: HTMLElement,
  ) {
    this.b = baueBuehne(
      "Übung Hören",
      (rolle) => (rolle === "start" ? this.start() : this.stopp("knopf")),
      (rolle) => (rolle === "pause" ? this.pause("knopf") : this.weiter()),
      u.zeigeTabelle ? () => u.zeigeTabelle?.() : undefined,
    );
    tafel.appendChild(this.b.wurzel);
    this.augen = u.sprechen ? new MorseAugenZu(u, tafel, () => this.zeichneModus()) : null;
    this.modusWahl = this.augen
      ? wahl<HoerModus>(
          AUGEN.modus.titel,
          [
            { id: "tippen", text: AUGEN.modus.tippen },
            { id: "augen", text: AUGEN.modus.augen },
          ],
          (id) => this.waehleModus(id),
        )
      : null;
    if (this.modusWahl) {
      this.modusWahl.wurzel.classList.add("morse-wahl--voll", "morse-modus");
      tafel.insertBefore(this.modusWahl.wurzel, this.b.wurzel);
      this.modus = liesModus();
    }
    this.ablauf = new Ablauf(u, "hoeren", this.b);
    const f = this.b.flaeche;

    // Ausgewertet: das Ergebnis der Runde
    this.fertigTeil = el("div", "morse-fertig");

    // Läuft: was gerade dran ist, das EINE Feld, die Frist
    this.laufTeil = el("div", "morse-lauf");
    this.phaseZeile = el("p", "morse-phase");
    const zeile = el("div", "morse-eingabe-zeile");
    const feld = el("label", "morse-feld");
    feld.append(el("span", "morse-feld-titel", "Was du hörst"));
    this.eingabe = el("input", "morse-eingabe");
    this.eingabe.type = "text";
    this.eingabe.autocomplete = "off";
    this.eingabe.spellcheck = false;
    this.eingabe.setAttribute("autocapitalize", "characters");
    this.eingabe.setAttribute("autocorrect", "off");
    this.eingabe.setAttribute("enterkeyhint", "next");
    this.eingabe.setAttribute("aria-describedby", "morse-hoeren-phase");
    this.phaseZeile.id = "morse-hoeren-phase";
    this.eingabe.maxLength = 40;
    this.eingabe.addEventListener("keydown", (ev) => this.feldTaste(ev));
    this.eingabe.addEventListener("input", () => this.eingegeben());
    feld.appendChild(this.eingabe);
    const fertig = knopf("", "morse-gruppe-fertig", () => this.werteOderWeiter("knopf"));
    fertig.setAttribute("aria-label", "Gruppe fertig (Enter)");
    fertig.append(el("span", "morse-gruppe-fertig-form"));
    // Der Tipp darf dem Feld den Fokus nicht nehmen — sonst schließt das iPhone die Tastatur (wie app.ts, Pre.1015).
    fertig.addEventListener("pointerdown", (ev) => ev.preventDefault());
    zeile.append(feld, fertig);
    this.frist = baueLeiste("morse-frist");
    // Was die Tasten JETZT tun — je Zustand (Prüfbefund W3: der Hinweis versprach mehr, als die Leertaste tat).
    this.tastenLauf = el("p", "morse-tastenhinweis");
    /*
     * Hilfe (Prüfbefund B2, Entscheidung 06.10.): „Nach dem Ton“ darf die Antwort NICHT verraten. Nach dem Ton steht
     * nur der Spickzettel „Deine Zeichen“ (Zeichen der Stufe mit ihrem Muster); das Muster der GANZEN Gruppe kommt
     * erst nach der Antwort, als Rückmeldung — vorher steht es gar nicht im DOM. „Vorher zeigen“ ist die
     * ausdrückliche Spickzettel-Wahl: das Gruppenmuster schon, während sie klingt. Beides steht UNTER dem Feld,
     * damit es nie rückt.
     */
    this.hilfeTeil = el("div", "morse-hilfe-teil");
    this.hilfeTitel = el("p", "morse-feld-titel");
    this.hilfeMuster = el("p", "morse-hilfe-muster");
    this.hilfeTeil.append(this.hilfeTitel, this.hilfeMuster);
    this.spick = baueSpick(u.stand().stufe);
    this.sprechen = u.sprechen ? new MorseSprechen(u.sprechen, this.sprechKontext(), () => u.jetzt()) : null;
    this.laufTeil.append(this.phaseZeile, zeile, this.frist, ...(this.sprechen ? [this.sprechen.wurzel] : []), this.hilfeTeil, this.spick, this.tastenLauf);

    // Die Gruppen der Runde, neueste oben
    this.liste = el("ol", "morse-gruppen");
    this.liste.setAttribute("aria-label", "Deine Gruppen");
    this.liste.setAttribute("aria-live", "polite");
    this.liste.setAttribute("reversed", "");

    // Bereit: Kennenlernen (wenn ein Zeichen neu ist), wie es geht, die Zeichen der Stufe, Gruppen oder Wörter, Hilfe
    this.bereitTeil = el("div", "morse-bereit");
    this.kennen = new MorseKennenlernen(u, (t) => this.b.melde(t, "fehler"), `reiter=${REITER}`);
    // Kein Zwang und nichts von selbst: die eigene Wahl bleibt, bis jemand auf „Vorgabe nehmen“ tippt.
    this.tempoHinweis = el("div", "morse-tempo-hinweis");
    this.tempoText = el("p", "morse-tempo-hinweis-text");
    this.tempoHinweis.append(this.tempoText, knopf(TEMPO.vorgabeNehmen, "morse-knopf morse-knopf--klein", () => u.vorgabeNehmen?.()));
    this.tempoHinweis.hidden = true;
    this.anleitung = el("p", "morse-anleitung");
    const zeichenBlock = el("div", "morse-zeichen-block");
    this.zeichenTitel = el("p", "morse-feld-titel", "Deine Zeichen — antippen zum Anhören");
    zeichenBlock.append(this.zeichenTitel);
    this.zeichenReihe = el("div", "morse-zeichenreihe");
    this.zeichenReihe.setAttribute("role", "group");
    this.zeichenReihe.setAttribute("aria-label", "Die Zeichen deiner Stufe");
    this.zeichenLegende = el("p", "morse-spick-legende", UMFANG.blass);
    this.zeichenLegende.hidden = true;
    zeichenBlock.append(this.zeichenReihe, this.zeichenLegende);
    this.umfangWahl = new MorseUmfang(u, () => this.zeichneBereit());
    this.artWahl = wahl<Art>(
      "Art der Übung",
      [
        { id: "gruppen", text: "Gruppen" },
        { id: "woerter", text: "Wörter" },
      ],
      (id) => {
        this.art = id;
        this.zeichneBereit();
      },
    );
    this.artHinweis = el("p", "morse-hinweis");
    this.artHinweis.hidden = true;
    const hilfeBlock = el("div", "morse-hilfe-wahl");
    this.hilfeWahl = wahl<HoerHilfe>(
      HILFE.titel,
      (["aus", "nach", "vorab", "immer"] as const).map((id) => ({ id, text: HILFE.hoerenWahl[id] })),
      (id) => {
        merkeHilfe("hoeren", id);
        this.zeichneBereit();
      },
    );
    this.hilfeWahl.wurzel.classList.add("morse-wahl--voll");
    this.hilfeText = el("p", "morse-hinweis");
    hilfeBlock.append(el("p", "morse-feld-titel", HILFE.titel), this.hilfeWahl.wurzel, this.hilfeText);
    const tasten = el("p", "morse-tastenhinweis", "Leertaste:\u00a0Start · Esc:\u00a0Stopp");
    // Neben „Start“, in derselben Zeile: die Vorführung zum Anhören — ohne etwas zu tippen (Hörspur, 07.10.2026). Sie ist
    // nachrangig (08.10.2026): am Rechner neben dem Hauptknopf, am Telefon klein darunter; in der Runde steht sie nicht da.
    this.vorfuehrung = u.hoerspur?.vorfuehrungPlatz("hoeren", () => u.stand()) ?? null;
    if (this.vorfuehrung) this.b.steuer.appendChild(this.vorfuehrung.wurzel);
    this.bereitTeil.append(this.tempoHinweis, this.kennen.wurzel, this.anleitung, ...(this.sprechen ? [el("p", "morse-hinweis", SPRECHEN.anleitung)] : []), this.umfangWahl.wurzel, zeichenBlock, this.artWahl.wurzel, this.artHinweis, hilfeBlock, tasten);

    // Die Zeile steht gleich unter dem Ergebnis der Runde, wo auch der Hinweis „Stufe …! Neu dazu“ herkommt. Der alte
    // Hinweis geht weg, sobald die Stufe geändert ist — er stimmte dann nicht mehr.
    this.stufeZeile = u.stufe ? new MorseStufe(u.stufe, { kompakt: true, beiWechsel: () => this.b.melde("") }) : null;
    if (this.stufeZeile) this.stufeZeile.wurzel.hidden = true;
    f.append(this.fertigTeil, ...(this.stufeZeile ? [this.stufeZeile.wurzel] : []), this.laufTeil, this.liste, this.bereitTeil);
    this.zeige("bereit", "");
    this.zeichneBereit();
    this.zeichneModus();
  }

  /** Der Modus: nur eine der beiden Bühnen steht da; mitten in einer Runde (in IRGENDEINER) ist die Wahl gesperrt. */
  private waehleModus(id: HoerModus): void {
    if (this.laeuft || this.pausiert) return;
    this.modus = id;
    merkeModus(id);
    this.zeichneModus();
  }

  private zeichneModus(): void {
    if (!this.augen || !this.modusWahl) return;
    const offen = this.laeuft || this.pausiert;
    this.modusWahl.setze(this.modus, new Map(), offen);
    this.modusWahl.wurzel.hidden = false;
    this.b.wurzel.hidden = this.modus === "augen";
    this.augen.wurzel.hidden = this.modus !== "augen";
    // Beide Bühnen haben ihre eigene „Üben mit“-Wahl: mitten in einer Runde (in IRGENDEINER) sind beide aus.
    this.umfangWahl.zeichne();
    this.augen.zeichneBereit();
  }

  /**
   * Die Phase der Gruppe. ⚠️ Jede Zuweisung geht durch den Zugriff: der Knopf „Sprechen“ zählt nur NACH dem Ton (das Mikrofon
   * würde ihn mithören, und iOS lässt Wiedergabe und Aufnahme nicht zugleich zu) und vergisst mit der Gruppe, was er gesagt hat.
   */
  private get phase(): Phase | null {
    return this.phaseWert;
  }
  private set phase(p: Phase | null) {
    this.phaseWert = p;
    this.sprechen?.zeige(p === "tippt" ? "tippt" : p === "hoert" ? "wartet" : "aus");
  }

  get laeuft(): boolean {
    return this.zustand === "laeuft" || this.augen?.laeuft === true;
  }
  get pausiert(): boolean {
    return this.zustand === "pause" || this.augen?.pausiert === true;
  }

  /* ── Bedienung ──────────────────────────────────────────────────── */

  start(): void {
    if (this.zustand === "laeuft" || this.zustand === "pause") return;
    const s = this.u.stand();
    const guete: Record<string, number> = {};
    for (const z of s.zeichen) guete[z.zeichen] = z.guete;
    // Womit geübt wird, legt der Start fest — die Wahl ist in der Runde gesperrt, und die Buchungen der Runde tragen es.
    this.umfang = this.umfangWahl.umfang();
    this.runde = hoerRunde(s.stufe, { art: this.art, guete, zufall: () => this.u.zufall(), umfang: this.umfang });
    // Wer übt, kennt das neue Zeichen ab jetzt — die Karte „Kennenlernen“ ist für diese Stufe erledigt (nicht bei „Alle Zeichen“:
    // dort muss das neue Zeichen nicht vorkommen).
    if (this.umfang === "meine") merkeKennengelernt(kochNeueZeichen(s.stufe));
    this.kennen.zeichne();
    this.zeiten = hoerZeiten(s.stufe);
    this.tempoBeimStart = `${s.einstellungen.tempo}/${s.einstellungen.wirksam}`;
    this.hilfe = liesHilfe(s.stufe).hoeren;
    this.hilfeTeil.hidden = this.hilfe === "aus";
    this.spick.hidden = this.hilfe === "aus";
    this.hilfeMuster.textContent = "";
    zeichneSpick(this.spick, s.stufe, { umfang: this.umfang, zeichen: s.zeichen });
    this.ablauf.neueRunde(this.umfang);
    this.stufeZeile?.zuruecksetzen();
    this.rundeNr++;
    this.gebucht = new Set();
    this.antworten = [];
    this.index = 0;
    this.liste.textContent = "";
    this.eingabe.value = "";
    this.b.melde("");
    this.zeige("laeuft", `Gruppe 1 von ${this.runde.gruppen.length}`);
    this.phaseText("Gleich geht's los …");
    // Noch in der Geste: so öffnet das iPhone die Tastatur (ein Fokus aus einer Uhr heraus öffnet sie nicht).
    this.eingabe.focus();
    insBild(this.b.wurzel);
    this.wachGeloest = false;
    // Läuft die Runde mit Hilfe, steht es im Journal (zählt trotzdem — Hilfe ist keine Fälschung).
    this.ablauf.halteWach(
      `start reiter=${REITER} art=${this.runde.art} stufe=${s.stufe} gruppen=${this.runde.gruppen.length} hilfe=${this.hilfe === "aus" ? "aus" : "an"}${this.hilfe === "vorab" ? " zeigen=vorab" : ""}${this.umfang === "alle" ? " umfang=alle" : ""}`,
    );
    this.ablauf.zeitLaeuft();
    this.ablauf.spaeter(VORLAUF_MS, () => void this.spieleGruppe(0));
  }

  stopp(grund: string): void {
    if (this.augen && (this.augen.laeuft || this.augen.pausiert)) {
      this.augen.stopp(grund);
      return;
    }
    if (this.zustand !== "laeuft" && this.zustand !== "pause") return;
    if (this.zaehltBeimStopp()) this.merkeAntwort();
    this.beende(grund);
  }

  /**
   * Zählt die laufende Gruppe beim Stopp (Prüfbefunde K4, M10)? Ganz gehört → ja, auch mit leerem Feld (dann
   * ehrlich falsch). Noch am Klingen oder pausiert → nur, wenn schon etwas getippt ist.
   */
  private zaehltBeimStopp(): boolean {
    if (!this.runde || this.antworten[this.index] !== undefined) return false;
    const getippt = this.getippt() !== "";
    if (this.zustand === "laeuft") return this.phase === "tippt" || (this.phase === "hoert" && getippt);
    return this.weiterBei === this.index && (this.pausiertIn === "tippt" || getippt);
  }

  pause(grund: string, gehtWeg = false): void {
    if (this.zustand !== "laeuft" || !this.runde) return;
    this.allesAus();
    this.ablauf.zeitHalt();
    this.weiterBei = this.phase === "rueckmeldung" ? this.index + 1 : this.index;
    this.pausiertIn = this.phase;
    this.phase = null;
    // Was getippt ist, bleibt im Feld (K4) — „Weiter“ spielt die Gruppe noch einmal dazu.
    this.ablauf.lassWach();
    this.u.spur(`pause reiter=${REITER} grund=${grund} gruppe=${this.index + 1}`);
    // Was beantwortet ist, sofort buchen — geht die Seite ganz, mit keepalive (W4).
    void this.bucheRest(gehtWeg);
    if (this.weiterBei >= this.runde.gruppen.length) {
      this.beende(grund);
      return;
    }
    this.zeige("pause");
    this.phaseText(
      this.weiterBei === this.index ? `Pause. „Weiter“ spielt Gruppe ${this.index + 1} noch einmal.` : `Pause. „Weiter“ spielt Gruppe ${this.weiterBei + 1}.`,
    );
  }

  weiter(): void {
    if (this.zustand !== "pause" || !this.runde) return;
    this.zeige("laeuft");
    this.phaseText("Gleich geht's weiter …");
    this.eingabe.focus();
    this.wachGeloest = false;
    this.ablauf.halteWach(`weiter reiter=${REITER}`);
    this.ablauf.zeitLaeuft();
    const bei = this.weiterBei;
    this.ablauf.spaeter(VORLAUF_MS, () => void this.spieleGruppe(bei));
  }

  leertaste(art: "keydown" | "keyup", wiederholt: boolean): boolean {
    if (this.augen && this.modus === "augen") return this.augen.leertaste(art, wiederholt);
    if (art === "keyup") return this.zustand !== "bereit";
    if (wiederholt) return true;
    if (this.zustand === "laeuft") this.stopp("leertaste");
    else if (this.zustand === "pause") this.weiter();
    else this.start();
    return true;
  }

  ruhe(grund: string, gehtWeg = false): void {
    this.pause(grund, gehtWeg);
    this.augen?.ruhe(grund, gehtWeg);
  }

  standGeaendert(): void {
    if (this.zustand !== "laeuft" && this.zustand !== "pause") this.zeichneBereit();
    this.augen?.standGeaendert();
  }

  /**
   * Die Hilfe in der Runde (B2). `wann`:
   *   - „klingt“: die Gruppe klingt gerade — bei „Vorher zeigen“ steht ihr Muster schon da, bei „Nach dem Ton“
   *     nichts (auch der Spickzettel noch nicht); bei „Immer“ steht der Spickzettel (nur er) die ganze Zeit da;
   *   - „verklungen“: Zeit zum Tippen — bei „Nach dem Ton“ NUR der Spickzettel, das Gruppenmuster nicht;
   *   - „beantwortet“: Enter oder Frist vorbei — jetzt das Muster der ganzen Gruppe als Rückmeldung.
   * Das Gruppenmuster wird nur gebaut, wenn es gezeigt werden darf; sonst ist es nicht im DOM.
   */
  private zeigeHilfe(gruppe: string, wann: "klingt" | "verklungen" | "beantwortet"): void {
    if (this.hilfe === "aus") return;
    const muster = wann === "beantwortet" || this.hilfe === "vorab";
    this.hilfeMuster.textContent = "";
    if (muster) {
      for (const z of zeichenVon(gruppe)) {
        const t = el("span", "morse-hilfe-zeichen");
        t.appendChild(musterBild(MORSE_TABELLE[z] ?? "", "m"));
        this.hilfeMuster.appendChild(t);
      }
    }
    this.hilfeTeil.dataset.zeigen = String(muster);
    this.hilfeTitel.textContent = !muster ? HILFE.musterTitel.wartet : wann === "beantwortet" ? HILFE.musterTitel.nach : HILFE.musterTitel.vorab;
    // „Immer“ (08.10.2026): die Karten „Deine Zeichen“ stehen vom Start an da und gehen nie weg.
    this.spick.hidden = this.hilfe === "nach" && wann === "klingt";
  }

  zerstoere(): void {
    this.augen?.zerstoere();
    this.allesAus();
    this.sprechen?.zerstoere();
    this.ablauf.lassWach();
  }

  /* ── Ablauf ─────────────────────────────────────────────────────── */

  private async spieleGruppe(i: number): Promise<void> {
    const r = this.runde;
    if (!r || this.zustand !== "laeuft") return;
    const tok = ++this.ablauf.token;
    this.index = i;
    this.wacheWieder();
    this.phase = "hoert";
    this.b.zaehler(`Gruppe ${i + 1} von ${r.gruppen.length}`);
    this.zeichneVerlauf();
    this.phaseText(r.art === "woerter" ? "Hör zu … (ein Wort)" : "Hör zu …");
    this.leisteAus();
    this.zeigeHilfe(r.gruppen[i], "klingt");
    const e = await this.u.spiele(r.gruppen[i]);
    if (tok !== this.ablauf.token) return;
    if (e === "kein-ton") return this.keinTon();
    if (e !== "fertig") return;
    // Der Ton ist verklungen: bei „Nach dem Ton“ jetzt der Spickzettel — das Gruppenmuster erst nach der Antwort.
    this.zeigeHilfe(r.gruppen[i], "verklungen");
    this.phase = "tippt";
    this.phaseText(r.art === "woerter" ? "Tipp das Wort — Enter: weiter" : "Tipp die Gruppe — Enter: weiter");
    const laenge = zeichenVon(r.gruppen[i]).length;
    const jetzt = this.u.jetzt();
    this.fristBis = zeichenVon(this.eingabe.value).length >= laenge ? jetzt + GNADE_MS : jetzt + eingabeZeitMs(laenge, this.zeiten);
    this.planeFrist();
  }

  private planeFrist(): void {
    const rest = Math.max(0, this.fristBis - this.u.jetzt());
    this.leisteAus();
    this.leisteHalt = laufeLeiste(this.frist, rest);
    this.ablauf.spaeter(rest, () => this.werte());
  }

  private feldTaste(ev: KeyboardEvent): void {
    if (ev.isComposing) return;
    if (ev.key !== "Enter" && ev.key !== " ") return;
    ev.preventDefault();
    // In der Pause heißt Enter oder Leertaste im Feld: weiter.
    if (this.zustand === "pause") {
      if (!ev.repeat) this.weiter();
      return;
    }
    // Die Leertaste schließt nur eine angefangene Gruppe ab (Gewohnheit aus LCWO: Gruppe, Leerzeichen, Gruppe).
    if (ev.key === " " && !this.eingabe.value.trim()) return;
    if (!ev.repeat) this.werteOderWeiter(ev.key === "Enter" ? "enter" : "leertaste");
  }

  private werteOderWeiter(_grund: string): void {
    if (this.zustand !== "laeuft") return;
    if (this.phase === "hoert" || this.phase === "tippt") this.werte();
    else if (this.phase === "rueckmeldung") this.naechste();
    this.eingabe.focus();
  }

  private eingegeben(): void {
    // Wer tippt, hat Vorrang: eine laufende Aufnahme endet, die Markierungen vom Sprechen stimmen nicht mehr.
    this.sprechen?.feldGeaendert();
    const v = this.eingabe.value;
    // Manche Tastaturen melden die Leertaste nur als Eingabe: dasselbe wie oben.
    if (/\s/.test(v)) {
      const ohne = v.replace(/\s+/g, "");
      this.eingabe.value = ohne;
      if (ohne && /\s$/.test(v) && (this.phase === "hoert" || this.phase === "tippt")) {
        this.werte();
        return;
      }
    }
    if (this.phase !== "tippt" || !this.runde) return;
    const laenge = zeichenVon(this.runde.gruppen[this.index]).length;
    this.fristBis = fristNachEingabe(this.u.jetzt(), this.fristBis, zeichenVon(this.eingabe.value).length, laenge);
    this.planeFrist();
  }

  /** Die Gruppe auswerten (Enter, Leertaste, Frist abgelaufen) — auch wenn sie noch klingt. */
  private werte(): void {
    const r = this.runde;
    if (!r || (this.phase !== "hoert" && this.phase !== "tippt")) return;
    this.allesAus();
    const tok = this.ablauf.token;
    const getippt = this.getippt() !== "";
    const a = this.merkeAntwort();
    this.phase = "rueckmeldung";
    // Beantwortet: jetzt erst das Muster der ganzen Gruppe (B2).
    this.zeigeHilfe(r.gruppen[this.index], "beantwortet");
    // Ein volles Bündel geht sofort an den Server (W4) — verwirft iOS die Seite, ist es schon gespeichert.
    this.bucheVolle();
    // „bei Fehler das Zeichen noch einmal langsam“ — höchstens drei; wer nichts getippt hat, sieht die Gruppe nur
    // (fünf Zeichen langsam hielten die Runde zu lange auf).
    const falsch = getippt ? [...new Set(a.zeichen.filter((z) => !z.richtig).map((z) => z.zeichen))].slice(0, NOCHMAL_HOECHSTENS) : [];
    const richtig = a.richtig === a.gesamt && a.zuviel === 0;
    this.phaseText(richtig ? "Richtig!" : falsch.length ? `Noch einmal langsam: ${falsch.join(" ")}` : "Nichts getippt — weiter geht's.");
    void (async () => {
      if (falsch.length) await this.u.spiele(falsch.join(" "), true);
      if (tok !== this.ablauf.token) return;
      this.ablauf.spaeter(this.zeiten.lueckeMs, () => this.naechste());
    })();
  }

  /** Die laufende Gruppe als beantwortet merken und zeigen. */
  private merkeAntwort(): Auswertung {
    const soll = this.runde!.gruppen[this.index];
    const ist = this.getippt();
    const a = kochAuswertung(soll, ist);
    this.antworten[this.index] = { soll, ist, auswertung: a };
    this.zeichneVerlauf();
    this.eingabe.value = "";
    this.zeigeAntwort(this.index, soll, a);
    return a;
  }

  private naechste(): void {
    const r = this.runde;
    if (!r || this.zustand !== "laeuft") return;
    this.allesAus();
    const i = this.index + 1;
    if (i >= r.gruppen.length) {
      this.beende("fertig");
      return;
    }
    void this.spieleGruppe(i);
  }

  private beende(grund: string): void {
    const r = this.runde;
    this.allesAus();
    this.ablauf.zeitHalt();
    this.phase = null;
    this.ablauf.lassWach();
    if (!r) return;
    const aufgaben = rundeAlsAufgaben(r, this.antworten);
    const beantwortet = this.antworten.filter(Boolean) as Antwort[];
    this.u.spur(`stopp reiter=${REITER} grund=${grund} gruppen=${beantwortet.length}/${r.gruppen.length} ton=${this.u.spieler.zustand()}`);
    this.zeige("fertig", `${beantwortet.length} von ${r.gruppen.length} Gruppen`);
    fokusAufStart(this.b);
    this.zeichneFertig(aufgaben, beantwortet, r.gruppen.length);
    // Der Bereich „Bereit“ steht wieder da — mit dem Stand von jetzt (ein Aufstieg mitten in der Runde bringt
    // die Karte „Kennenlernen“ für das neue Zeichen).
    this.zeichneBereit();
    if (!aufgaben.length) {
      this.b.melde("Keine Gruppe beantwortet — nichts gespeichert.", "info");
      return;
    }
    const nr = this.rundeNr;
    this.b.melde("Speichere …", "info");
    void this.bucheRest(false).then(() => {
      if (nr !== this.rundeNr || this.zustand !== "fertig") return;
      this.zeichneBereit();
      if (this.ablauf.hatOffenes) {
        this.b.melde("");
        return;
      }
      const d = this.ablauf.aufstieg;
      if (!d) {
        this.b.melde("Gespeichert.", "info");
        return;
      }
      const neu = kochNeuesZeichen(d.stufe);
      const e = this.u.stand().einstellungen;
      const schneller = `${e.tempo}/${e.wirksam}` !== this.tempoBeimStart ? TEMPO.schneller(e.tempo, e.wirksam) : "";
      this.b.melde(`Stufe ${d.stufe}! Neu dazu: ${neu} (${musterSprechen(MORSE_TABELLE[neu])}). Lern es unten kennen, bevor du weiterübst.${schneller}`, "gut");
      this.u.feiere?.(d);
      // Nur nach einem Aufstieg und nur, wenn es etwas zurückzugehen gibt.
      if (this.stufeZeile && this.u.stand().stufe >= 2) {
        this.stufeZeile.zuruecksetzen();
        this.stufeZeile.zeichne(this.u.stand());
        this.stufeZeile.wurzel.hidden = false;
      }
    });
  }

  /** Die vollen Bündel buchen, die noch nicht gebucht sind (gleich nach der fünften Gruppe). */
  private bucheVolle(): void {
    const r = this.runde;
    if (!r) return;
    const z = zuBuchen(r, this.antworten, this.gebucht, true);
    if (!z.aufgaben.length) return;
    for (const g of z.gruppen) this.gebucht.add(g);
    void this.ablauf.sende(this.ablauf.satz(z.aufgaben));
  }

  /** Alles Beantwortete, das noch nicht gebucht ist (Pause, Stopp, Ende, Seite verdeckt). Wartet, bis alles da ist. */
  private async bucheRest(gehtWeg: boolean): Promise<void> {
    const r = this.runde;
    if (r) {
      const z = zuBuchen(r, this.antworten, this.gebucht);
      if (z.aufgaben.length) {
        for (const g of z.gruppen) this.gebucht.add(g);
        void this.ablauf.sende(this.ablauf.satz(z.aufgaben), gehtWeg);
      }
    }
    await this.ablauf.warte();
  }

  private keinTon(): void {
    const zustand = this.u.spieler.zustand();
    this.u.spur(`kein-ton reiter=${REITER} zustand=${zustand} gruppe=${this.index + 1}`);
    const r = this.runde;
    this.allesAus();
    this.ablauf.zeitHalt();
    this.phase = null;
    this.ablauf.lassWach();
    // Was schon beantwortet ist, geht nicht verloren — buchen; die Statuszeile gehört dem Ton.
    if (r) void this.bucheRest(false);
    this.zeige("fehler", "");
    fokusAufStart(this.b);
    this.b.melde(keinTonText(zustand), "fehler");
  }

  /* ── Anzeige ────────────────────────────────────────────────────── */

  private zeige(z: BuehnenZustand, zaehler?: string): void {
    this.zustand = z;
    this.b.setze(z, { zaehler });
    this.bereitTeil.hidden = z === "laeuft" || z === "pause";
    if (this.vorfuehrung) this.vorfuehrung.wurzel.hidden = z === "laeuft" || z === "pause";
    if (z !== "fertig" && this.stufeZeile) this.stufeZeile.wurzel.hidden = true;
    this.laufTeil.hidden = !(z === "laeuft" || z === "pause");
    this.fertigTeil.hidden = z !== "fertig";
    this.liste.hidden = z === "bereit" || z === "fehler" || this.liste.childElementCount === 0;
    this.zeichneModus();
    this.zeichneVerlauf();
    this.tastenLauf.textContent =
      z === "pause" ? "Leertaste oder Enter:\u00a0Weiter · Esc:\u00a0Stopp" : "Im\u00a0Feld: Enter\u00a0=\u00a0nächste\u00a0Gruppe · Esc\u00a0=\u00a0Stopp";
  }

  /** Der Verlauf der Runde (morse-balken.ts): eine Gruppe je Strich — in der Runde und nach dem Stopp, sonst aus. */
  private zeichneVerlauf(): void {
    const r = this.runde;
    if (!r || this.zustand === "bereit" || this.zustand === "fehler") return this.b.verlauf(null);
    const ergebnisse = r.gruppen.map((_, i) => {
      const a = this.antworten[i]?.auswertung;
      return a ? a.richtig === a.gesamt && a.zuviel === 0 : undefined;
    });
    this.b.verlauf(rundenVerlauf(ergebnisse, { gesamt: r.gruppen.length, jetzt: this.zustand === "fertig" ? null : this.index }));
  }

  private phaseText(t: string): void {
    if (this.phaseZeile.textContent !== t) this.phaseZeile.textContent = t;
    this.phaseZeile.dataset.phase = this.phase ?? (this.zustand === "pause" ? "pause" : "vorlauf");
  }

  private zeichneBereit(): void {
    const s = this.u.stand();
    this.stufeZeile?.zeichne(s);
    const neu = kochNeuesZeichen(s.stufe);
    this.kennen.zeichne();
    // K1: eigenes Tempo schneller (wirksam) als die Vorgabe der Anfänger-Stufe? Dann EINE Zeile mit dem Angebot.
    const v = kochTempoVorgabe(s.stufe);
    const zuSchnell = s.stufe <= KOCH_ANFAENGER_BIS_STUFE && s.einstellungen.wirksam > v.wirksam && typeof this.u.vorgabeNehmen === "function";
    this.tempoHinweis.hidden = !zuSchnell;
    if (zuSchnell) this.tempoText.textContent = TEMPO.zuSchnell(v.tempo, v.wirksam);
    const hilfe = liesHilfe(s.stufe).hoeren;
    this.hilfeWahl.setze(hilfe);
    this.hilfeText.textContent = HILFE.hoeren[hilfe];
    const alle = this.umfangWahl.zeichne() === "alle";
    this.zeichenReihe.textContent = "";
    this.zeichenReihe.dataset.umfang = alle ? "alle" : "meine";
    this.zeichenTitel.textContent = alle ? UMFANG.zeichenTitel : "Deine Zeichen — antippen zum Anhören";
    this.zeichenReihe.setAttribute("aria-label", alle ? UMFANG.zeichenGruppe : "Die Zeichen deiner Stufe");
    // Neu sind auf Stufe 1 beide (K und M), danach je eins. Bei „Alle Zeichen“ gibt es kein „neu“; was noch nie gehört wurde, ist blass.
    const neue = alle ? [] : kochNeueZeichen(s.stufe);
    const gehoert = new Set(s.zeichen.filter((z) => z.gehoert > 0).map((z) => z.zeichen));
    let blass = 0;
    for (const z of kochUebungsZeichen(s.stufe, alle ? "alle" : "meine")) {
      const leise = alle && !gehoert.has(z);
      if (leise) blass++;
      this.zeichenReihe.appendChild(zeichenKnopf(z, () => this.hoere(z), { neu: neue.includes(z), leise }));
    }
    this.zeichenLegende.hidden = blass === 0;
    // Wörter gibt es bei „Alle Zeichen“ auf jeder Stufe (alle Buchstaben sind dabei); sonst erst, wenn die Stufe genug kennt.
    const wortAb = ersteWortStufe();
    const ohneWoerter = !alle && s.stufe < wortAb;
    if (ohneWoerter) this.art = "gruppen";
    this.artWahl.setze(this.art, ohneWoerter ? new Map([["woerter", `ab Stufe ${wortAb}`]]) : new Map());
    const ersatz = !alle && this.art === "woerter" && !woerterMitNeu(s.stufe);
    this.artHinweis.textContent = ersatz ? `Auf dieser Stufe gibt es kein Wort mit ${neu} — darum Gruppen.` : "";
    this.artHinweis.hidden = !ersatz;
    this.anleitung.textContent = this.art === "woerter" && !ersatz ? ANLEITUNG.hoerenWoerter : ANLEITUNG.hoerenGruppen;
  }

  private zeigeAntwort(i: number, soll: string, a: Auswertung): void {
    const li = el("li", "morse-gruppe");
    li.value = i + 1;
    li.dataset.richtig = String(a.richtig === a.gesamt && a.zuviel === 0);
    const falsch = a.zeichen.filter((z) => !z.richtig);
    const sprich = el(
      "span",
      "morse-nur-vorlesen",
      `Gruppe ${i + 1}: ${a.richtig} von ${a.gesamt} richtig${falsch.length ? `. Falsch: ${falsch.map((z) => (z.statt ? `${z.zeichen}, du hattest ${z.statt}` : `${z.zeichen} ausgelassen`)).join("; ")}` : ""}${a.zuviel ? `. ${a.zuviel} zu viel` : ""}.`,
    );
    const bild = el("span", "morse-gruppe-bild");
    bild.setAttribute("aria-hidden", "true");
    bild.append(el("span", "morse-gruppe-nr", String(i + 1)), vergleich(soll, a), el("span", "morse-gruppe-wert", `${a.richtig}/${a.gesamt}${a.zuviel ? ` +${a.zuviel}` : ""}`));
    li.append(sprich, bild);
    this.liste.prepend(li);
    this.liste.hidden = false;
  }

  private zeichneFertig(aufgaben: Array<{ soll: string; ist: string }>, beantwortet: Antwort[], gesamt: number): void {
    const t = this.fertigTeil;
    t.textContent = "";
    if (!aufgaben.length) {
      t.appendChild(el("p", "morse-ergebnis-text", "Diese Runde ist ohne Antwort zu Ende."));
      return;
    }
    const e = rundeErgebnis(aufgaben);
    const kopf = el("div", "morse-ergebnis");
    kopf.append(
      el("p", "morse-ergebnis-zahl", prozent(e.quote)),
      el(
        "p",
        "morse-ergebnis-text",
        `${e.richtig} von ${e.gesamt} Zeichen richtig${e.zuviel ? ` · ${e.zuviel} zu viel` : ""} · ${beantwortet.length}${beantwortet.length < gesamt ? ` von ${gesamt}` : ""} ${
          this.runde?.art === "woerter" ? "Wörter" : "Gruppen"
        } · ${dauerText(this.ablauf.aktivMs)}`,
      ),
    );
    t.appendChild(kopf);
    // Eine Runde mit allen Zeichen sagt es: sie zählt für die Güte jedes Zeichens, nicht für den Aufstieg.
    if (this.umfang === "alle") t.appendChild(el("p", "morse-hinweis", UMFANG.fertig));
    const unsicher = unsichereZeichen(beantwortet.map((a) => a.auswertung));
    if (unsicher.length) {
      const block = el("div", "morse-zeichen-block");
      block.append(el("p", "morse-feld-titel", "Noch unsicher — antippen zum Anhören"));
      const reihe = el("div", "morse-zeichenreihe");
      for (const x of unsicher.slice(0, 8)) reihe.appendChild(zeichenKnopf(x.zeichen, () => this.hoere(x.zeichen, true), { zusatz: `${x.falsch}× falsch` }));
      block.appendChild(reihe);
      t.appendChild(block);
    } else {
      t.appendChild(el("p", "morse-ergebnis-text", "Alles richtig — stark."));
    }
  }

  /** Ein Zeichen zum Anhören (außerhalb der Runde) — kam kein Ton, steht es in der Statuszeile. */
  private hoere(z: string, langsam = false): void {
    if (this.zustand !== "laeuft") void hoereMitMeldung(this.u, z, (t) => this.b.melde(t, "fehler"), `reiter=${REITER} abspielen`, langsam);
  }

  /* ── Sprechen ───────────────────────────────────────────────────── */

  /** Was im Feld steht, ohne die Platzhalter des Sprechens (·) — das ist die Antwort. */
  private getippt(): string {
    return this.eingabe.value.split(LUECKE).join("").trim();
  }

  /** Was „Sprechen“ vom Reiter braucht (morse-sprechen.ts). */
  private sprechKontext(): SprechKontext {
    return {
      // Frei ist, was die Gruppe lang ist, abzüglich der Zeichen UND Lücken, die schon im Feld stehen.
      platz: () => {
        const gruppe = this.runde?.gruppen[this.index];
        if (gruppe === undefined) return 0;
        const schon = zeichenVon(this.eingabe.value).length + this.eingabe.value.split(LUECKE).length - 1;
        return Math.max(0, zeichenVon(gruppe).length - schon);
      },
      erlaubt: () => new Set(kochUebungsZeichen(this.u.stand().stufe, this.umfang)),
      feld: () => this.eingabe.value,
      setzeFeld: (t) => {
        this.eingabe.value = t.slice(0, this.eingabe.maxLength);
      },
      beiStart: () => {
        // Das Mikrofon hört zu: die Frist ruht (Sprechen und Auswerten brauchen ihre Zeit), der Ton darf schlafen.
        this.leisteAus();
        this.ablauf.uhrAus();
        this.ablauf.lassWach();
        this.wachGeloest = true;
      },
      beiEnde: (o) => this.sprechenEnde(o),
      spur: (t) => this.u.spur(`${t} reiter=${REITER}`),
    };
  }

  /**
   * Die Aufnahme ist vorbei (oder ein Zeichen wurde aus der Auswahl gesetzt). Die Gruppe wird NICHT abgeschickt: das Ergebnis
   * steht im Feld, und die Frist beginnt neu — zum Prüfen länger als nach getipptem Text, mit leeren Stellen noch länger; nach
   * einem Fehler eine volle Tipp-Zeit.
   */
  private sprechenEnde(o: { gefuellt: boolean; offen: number }): void {
    if (this.zustand !== "laeuft" || this.phase !== "tippt" || !this.runde) return;
    this.wacheWieder();
    this.eingabe.focus({ preventScroll: true });
    const laenge = zeichenVon(this.runde.gruppen[this.index]).length;
    const dauer = o.gefuellt ? (o.offen > 0 ? SPRECH_PRUEFEN_UNSICHER_MS : SPRECH_PRUEFEN_MS) : eingabeZeitMs(laenge, this.zeiten);
    this.fristBis = this.u.jetzt() + dauer;
    this.planeFrist();
  }

  /** Nach dem Mikrofon den Ton wieder für die Runde wach halten (das Mikrofon hatte ihn aus der Pflicht genommen). */
  private wacheWieder(): void {
    if (!this.wachGeloest) return;
    this.wachGeloest = false;
    this.ablauf.halteWach(`sprechen-ende reiter=${REITER}`);
  }

  /* ── Uhr, Ton, Zeit ─────────────────────────────────────────────── */

  private leisteAus(): void {
    this.leisteHalt?.();
    this.leisteHalt = null;
  }

  /** Sofort still: keine Fortsetzung gilt mehr, keine Uhr, keine Leiste, kein Ton. */
  private allesAus(): void {
    this.sprechen?.abbrechen();
    this.ablauf.allesAus();
    this.leisteAus();
  }
}
